import { Reveal, Eyebrow, MaskLines } from "./Reveal";

const B = ({ children }) => <strong className="font-semibold text-lime">{children}</strong>;

const cards = [
  {
    span: "md:col-span-4", tag: "01 / Explore", big: true,
    body: <>This website explores <B>science, rational thinking, scientific skepticism, philosophy, and sentientism</B>. Our aim is simple: to promote a deeper understanding of the world through evidence, reason, and curiosity.</>,
  },
  {
    span: "md:col-span-2", tag: "02 / Method",
    body: <>Science is not merely a collection of facts. It is a method—a disciplined way of asking questions about nature and testing our ideas against reality. Through observation, experimentation, and critical thinking, science helps us gradually uncover how the universe works and how we fit within it.</>,
  },
  {
    span: "md:col-span-3", tag: "03 / Skepticism",
    body: <>At Desi Science Page, we embrace <B>scientific skepticism</B>. Extraordinary claims deserve careful examination, and beliefs should stand on the strength of evidence. By questioning assumptions and evaluating evidence critically, we can separate what is true from what only appears convincing.</>,
  },
  {
    span: "md:col-span-3", tag: "04 / Curiosity",
    body: <>Curiosity drives everything we do. Here you will find discussions ranging from <B>scientific discoveries and cognitive biases to philosophy, ethics, and the psychology of belief</B>. At the same time, we examine <B>pseudoscience, misinformation, and quackery</B>—because misleading claims can cause real harm. Our goal is to provide information that is reliable, evidence-based, and intellectually honest.</>,
  },
  {
    span: "md:col-span-2", tag: "05 / Sentientism", accent: true,
    body: <>Desi Science Page also embraces the principles of <B>sentientism</B>, recognizing that all sentient beings—humans, animals, and potentially future forms of intelligence capable of experience—deserve moral consideration and ethical treatment.</>,
  },
  {
    span: "md:col-span-4", tag: "06 / Compassion",
    body: <>Understanding the world through <B>science, reason, and compassion</B> can help us build a better future.<span className="block mt-5 text-zinc-300">Whether you are a lifelong science enthusiast or someone just beginning to explore rational thinking, this space is for you.</span></>,
    big: true,
  },
];

const Card = ({ c, i }) => (
  <Reveal delay={(i % 2) * 0.1} className={`${c.span} h-full`}>
    <article
      data-testid={`about-card-${i + 1}`}
      className={`group relative h-full rounded-3xl border p-7 sm:p-9 overflow-hidden transition-colors duration-500 ${c.accent ? "accent-card bg-lime text-ink border-lime" : "bg-panel border-white/10 hover:border-lime/50"}`}
    >
      <div className={`absolute -right-20 -top-20 w-56 h-56 rounded-full blur-3xl transition-opacity duration-700 opacity-0 group-hover:opacity-100 ${c.accent ? "bg-white/40" : "bg-lime/15"}`} />
      <p className={`relative font-mono text-[12px] uppercase tracking-[0.25em] mb-6 ${c.accent ? "text-ink/70" : "text-zinc-400"}`}>{c.tag}</p>
      <p className={`relative leading-relaxed ${c.big ? "font-display text-xl sm:text-2xl lg:text-[1.7rem] leading-snug text-white" : "text-base"} ${c.accent ? "text-ink [&_strong]:text-ink [&_strong]:underline" : c.big ? "" : "text-zinc-300"}`}>
        {c.body}
      </p>
    </article>
  </Reveal>
);

export const About = () => (
  <section id="about" data-testid="about-section" className="relative px-5 sm:px-10 lg:px-16 py-24 sm:py-36">
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
      <div>
        <Eyebrow className="mb-5">// Evidence · Reason · Curiosity</Eyebrow>
        <h2 className="font-display font-bold uppercase text-4xl sm:text-6xl lg:text-7xl leading-[0.9]">
          <MaskLines lines={["Science, reason", <span className="text-lime">& compassion</span>]} />
        </h2>
      </div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-6 gap-4 md:auto-rows-fr">
      {cards.map((c, i) => <Card key={i} c={c} i={i} />)}
    </div>
  </section>
);
